import React, { forwardRef, useMemo, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { get } from 'lodash-es';
import CommonTable from '@/pages/dashboard/components/CommonTable';
import type { ICommonTableRef } from '@/pages/dashboard/components/CommonTable/type';
import { expandColumnsForDownload } from '@/pages/dashboard/components/CommonTable/utils';
import { useDownloadDataSource } from '@/pages/dashboard/components/CommonTable/hooks/useDownloadDataSource';
import Render from '@/pages/dashboard/components/PageContainer/Render';
import { useLocal, useSelectorMap } from '@/pages/dashboard/utils/customHook';
import { getLanguage, getName } from '@home/utils/common';
import { partitionPrWorkspaceTableOperations } from '@home/pages/studio/pr/prTableOperationUtils';
import { channelInfo } from '@op/components/ChannelLabel';
import classNames from 'classnames';
import { normalizePrPressTableResponse } from '../../_utils';
import {
  DEFAULT_DATA_KEY,
  METRIC_CODES,
  REGION_METRIC_CODES,
  STREAM_METRIC_CODES,
} from './_constants';
import type { IStackedBarTooltipData, PrMediaTableProps } from './_type';
import { formatValueForRule, isNestedMetricValue, pickChannels } from './_utils';
import { usePrMediaKeywordSearch } from './_hooks/usePrMediaKeywordSearch';
import { usePrMediaTableColumns } from './_hooks/usePrMediaTableColumns';
import { useToolbarHost } from './_hooks/useToolbarHost';
import './index.scss';

export type { PrMediaColumnCode, PrMediaCellRenderContext, PrMediaTableMode, PrMediaTableProps } from './_type';
export type { TPlatformTotalRow } from './_type';

const PrMediaTable = forwardRef<ICommonTableRef, PrMediaTableProps>((props, ref) => {
  const {
    cellRenderers,
    showSearch = true,
    colorByCode: colorByCodeProp,
    mode: tableMode = 'media',
    cardLinkEnabled = false,
    data,
    dataKey = DEFAULT_DATA_KEY,
    totalRecordPath = 'row_count',
    changeData,
    commonBaseTableProps: commonBaseIn,
    metricsCardWrapProps: metricsCardIn,
    ...rest
  } = props;
  const {
    titleEn = 'Media Breakdown',
    titleCn = '媒体分布',
    operation: metricsOperation,
    contentHeader: pageContentHeader,
    ...restMetrics
  } = metricsCardIn || {};
  const { legendSlot, otherSlot } = partitionPrWorkspaceTableOperations(metricsOperation);
  const { columns: columnConfigFromPage, style: userTableStyle, ...restCommonBase } = commonBaseIn || {};
  const { className: incomingTableClassName, ...restForCommon } = rest as { className?: string; } & typeof rest;
  const [stackedBarTooltip, setStackedBarTooltip] = useState<IStackedBarTooltipData | null>(null);
  const { getFilterItemText } = useSelectorMap();
  const { toolbarHost, inTabToolbar } = useToolbarHost();
  const { isEN } = useLocal();
  const apiBlock = (data as Record<string, unknown>)?.[dataKey];
  const apiData = useMemo(() => {
    const d = (apiBlock as { data?: Record<string, unknown> } | undefined)?.data;
    if (!d || typeof d !== 'object') {
      return undefined;
    }
    return normalizePrPressTableResponse(d) as Record<string, unknown>;
  }, [apiBlock]);
  const apiLoading = Boolean((apiBlock as { loading?: boolean; } | undefined)?.loading);
  const totalRecord = Number(get(apiData, totalRecordPath)) || 0;
  const rows = (apiData?.metrics_list as Record<string, unknown>[]) || [];
  const channels = useMemo(() => pickChannels(apiBlock), [apiBlock]);

  const colMaxes = useMemo(() => {
    const m: Record<string, number> = {};
    const activeCodes =
      tableMode === 'regions'
        ? REGION_METRIC_CODES
        : tableMode === 'streams'
          ? STREAM_METRIC_CODES
          : METRIC_CODES;
    for (const code of activeCodes) {
      m[code] = Math.max(
        0,
        ...rows.map((r) => {
          const raw = r[code];
          if (isNestedMetricValue(raw)) return raw.total || 0;
          return Number(raw) || 0;
        }),
      );
    }
    return m;
  }, [rows, tableMode]);

  const { searchNode } = usePrMediaKeywordSearch({
    data,
    changeData,
    showSearch,
    isEN,
    inTabToolbar,
  });

  const rowPage = Number((data as { extraParams?: { page?: number; }; })?.extraParams?.page) || 1;
  const pageSize = Number((data as { extraParams?: { page_size?: number; }; })?.extraParams?.page_size) || 10;

  const columns = usePrMediaTableColumns({
    tableMode,
    isEN,
    colMaxes,
    channels,
    colorByCodeProp,
    cardLinkEnabled,
    cellRenderers,
    columnConfigFromPage,
    rowPage,
    pageSize,
    setStackedBarTooltip,
  });

  /**
   * CommonTable 用 dataPath 从原始 Service.data 取值；列表接口若仅有 `rows` 而无 `metrics_list`，
   * 会直接空表。normalize 已与下方 rows 对齐，此处显式传 dataSource 并带出后端分页总数。
   */
  const mergedTableConfig = useMemo(() => {
    const tc = restCommonBase.tableConfig;
    if (!tc) return undefined;
    const pagination = tc.pagination;
    if (!pagination?.backendPaging) {
      return tc;
    }
    return {
      ...tc,
      pagination: {
        ...pagination,
        total: totalRecord > 0 ? totalRecord : rows.length,
      },
    };
  }, [restCommonBase.tableConfig, totalRecord, rows.length]);

  const downloadColumns = useMemo(
    () => expandColumnsForDownload(columns, isEN),
    [columns, isEN],
  );
  const downloadDataSource = useDownloadDataSource(rows, columns, getFilterItemText);

  const showOpsInTabToolbar =
    inTabToolbar && otherSlot.length > 0;

  const tabToolbarPortal: ReactNode =
    inTabToolbar && toolbarHost
      ? (createPortal(
        <div className="dbh-pr-workspace-tabs__toolbar-inner">
          {searchNode}
          {showOpsInTabToolbar
            ? otherSlot.map((item, index) => {
              const opName = (item as { name?: string; }).name;
              return (
                <Render
                  key={`pr-media-header-op-${opName ?? 'op'}-${index}`}
                  config={item as { name: string; type: string;[k: string]: unknown; }}
                  tableColumns={downloadColumns}
                  tableTitle={getName(isEN, titleEn, titleCn)}
                  tableData={downloadDataSource}
                  data={data}
                  changeData={changeData}
                  {...(opName === 'DownloadButton' || opName === 'PrDownloadButton'
                    ? { disabled: apiLoading || totalRecord <= 0 }
                    : {})}
                  {...restForCommon}
                />
              );
            })
            : null}
        </div>,
        toolbarHost,
      ) as ReactNode)
      : null;

  const contentHeaderInCard = (
    <>
      {!inTabToolbar ? searchNode : null}
      {pageContentHeader as React.ReactNode}
    </>
  );

  return (
    <div className="dbh-pr-media-table__root">
      {tabToolbarPortal}
      <CommonTable
        ref={ref}
        className={classNames('dbh-pr-media-table', {
          'dbh-pr-media-table--articles':
            tableMode === 'articles' ||
            tableMode === 'posts' ||
            tableMode === 'videos' ||
            tableMode === 'streams',
        }, incomingTableClassName)}
        data={data}
        dataKey={dataKey}
        totalRecordPath={totalRecordPath}
        changeData={changeData}
        commonBaseTableProps={{
          ...restCommonBase,
          dataSource: rows,
          tableConfig: mergedTableConfig ?? restCommonBase.tableConfig,
          /** pageConfig 中 minHeight:360 会与实际行数无关地撑高；需在 ...userTableStyle 之后覆盖 */
          style: {
            overflowX: 'auto',
            ...(userTableStyle || {}),
            maxHeight: 'none',
            minHeight: 'unset',
          },
          height: 360 as unknown as number,
          columns,
        }}
        metricsCardWrapProps={{
          ...restMetrics,
          titleEn,
          titleCn,
          contentHeader: contentHeaderInCard,
          operation: inTabToolbar ? (legendSlot.length ? (legendSlot as never) : undefined) : metricsOperation,
        }}
        {...restForCommon}
      />
      {stackedBarTooltip
        ? (createPortal(
          <div
            className="dbh-pr-media-table__stacked-tooltip"
            style={{ left: stackedBarTooltip.position.x, top: stackedBarTooltip.position.y }}
          >
            <div className="dbh-pr-media-table__stacked-tooltip-head">
              <span className="dbh-pr-media-table__stacked-tooltip-source">{getLanguage(isEN, 'Source', '来源')}</span>
              <span className="dbh-pr-media-table__stacked-tooltip-col">{stackedBarTooltip.columnLabel}</span>
            </div>
            <ul className="dbh-pr-media-table__stacked-tooltip-list">
              {stackedBarTooltip.entries.map((item) => {
                const { img: iconSrc, name } = channelInfo(item.channel as never, undefined, false);
                return (
                  <li key={item.channel} className="dbh-pr-media-table__stacked-tooltip-item">
                    {/* <span
                      className="dbh-pr-media-table__stacked-tooltip-dot"
                      style={{ backgroundColor: item.color }}
                    /> */}
                    <img
                      className="dbh-pr-media-table__stacked-tooltip-icon"
                      src={iconSrc}
                      alt=""
                    />
                    <span className="dbh-pr-media-table__stacked-tooltip-name">{name}</span>
                    <span className="dbh-pr-media-table__stacked-tooltip-val">
                      {formatValueForRule(item.value, stackedBarTooltip.formatRule)}
                    </span>
                  </li>
                );
              })}
            </ul>
            <div className="dbh-pr-media-table__stacked-tooltip-foot">
              <span>{getLanguage(isEN, 'Total', '合计')}</span>
              <span>{formatValueForRule(stackedBarTooltip.total, stackedBarTooltip.formatRule)}</span>
            </div>
          </div>,
          document.body,
        ) as ReactNode)
        : null}
    </div>
  );
});

PrMediaTable.displayName = 'PrMediaTable';

export default PrMediaTable;
